import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInViewport } from 'react-in-viewport';
import { connect } from 'react-redux';
import memoize from 'fast-memoize';

import SmartImageLib, { defaultImgState, State as ImageState } from 'Lib/SmartImageLib';
import isNil from 'Lib/utils/isNil';
import shallowEqual from 'Lib/utils/shallowEqual';
import shallowEqualNested from 'Lib/utils/shallowEqualNested';
import usePrevious from 'Hooks/usePrevious';

import { trackEvent as _trackEvent, type TrackEvent } from 'Store/sparks/actions';

import type { CroppingAttributes } from 'Components/FocusedImage/types';

import ErrorPlaceholder from './components/ErrorPlaceholder';
import Loader from './components/Loader';
import useDownloadImages from './hooks/useDownloadImages';
import useImage from './hooks/useImage';
import { ImageErrorPlaceholderType, ImageLoaderType, Loaders } from './constants';
import { getEffectiveUrls } from './utils';

const isNativeLazyLoadSupported = memoize(
  () => __CLIENT__ && 'loading' in HTMLImageElement.prototype
);

type Props = {
  className?: string;
  customLoader?: React.ReactElement;
  draggable?: boolean;
  errorPlaceholder?: ImageErrorPlaceholderType;
  index?: number;
  loader?: ImageLoaderType;
  onClick?: React.MouseEventHandler;
  onError?: (url: string, error: any) => void;
  onLoaded?: (index?: number) => void;
  shouldExcludeAriaLabel?: boolean;
  shouldTrackPerf?: boolean;
  urls: string[]; // An array of image urls ordered from lowest -> highest quality
  transformWebp?: boolean;
  trackEvent?: TrackEvent;
  'data-testid'?: string;
  width?: number;
  height?: number;
} & React.ImgHTMLAttributes<HTMLImageElement> &
  CroppingAttributes;

const SmartImage = (props: Props) => {
  const {
    alt,
    className,
    customLoader,
    draggable,
    errorPlaceholder,
    height,
    index,
    intendedAspectRatio,
    interestBottom,
    interestLeft,
    interestRight,
    interestTop,
    interestZoom,
    loader = Loaders.NONE,
    onClick,
    onError,
    onLoaded,
    shouldExcludeAriaLabel,
    shouldTrackPerf,
    loading,
    style,
    trackEvent,
    transformWebp,
    urls,
    width,
    'data-testid': testId,
  } = props;
  const [imageState, setImageState] = useState<ImageState>(defaultImgState);
  const previousUrls = usePrevious(urls);

  const imgLib = useRef(
    new SmartImageLib((newState) => {
      if (!shallowEqual(imageState, newState)) {
        setImageState(newState);
      }
    }, onError)
  );
  const emptyRef = useRef(null);
  const urlsToDownload = useMemo(
    () => getEffectiveUrls(urls, { webp: transformWebp }),
    [urls, transformWebp]
  );
  const shouldLazyLoad = loading === 'lazy';

  const onImageLoad = useCallback(() => {
    onLoaded?.(index);
  }, [index, onLoaded]);

  const { node, ref } = useImage({
    alt,
    className,
    style,
    draggable,
    height,
    width,
    intendedAspectRatio,
    interestBottom,
    interestLeft,
    interestRight,
    interestTop,
    interestZoom,
    loading,
    onClick,
    trackEvent,
    onImageLoad,
    shouldExcludeAriaLabel,
    shouldTrackPerf,
    'data-testid': testId,
    ...imageState,
  });
  const { isLoading, isError, imgSrc: imageResult } = imageState;

  const shouldUseNativeLazyLoad = useMemo(() => {
    // native lazy load doesn't support background image
    const isBackgroundImage = !isNil(intendedAspectRatio);
    return isNativeLazyLoadSupported() && shouldLazyLoad && !isBackgroundImage;
  }, [shouldLazyLoad, intendedAspectRatio]);

  const { inViewport } = useInViewport(
    // don't bind intersection observer if not enabled
    shouldLazyLoad ? ref : emptyRef,
    { threshold: 0.25 },
    { disconnectOnLeave: true }
  );

  useDownloadImages(imgLib.current, {
    loaded: !!imageResult,
    shouldLazyLoad: !shouldUseNativeLazyLoad && shouldLazyLoad,
    inViewport,
    urls: urlsToDownload,
  });

  const shouldRenderLoader = isLoading || !imageResult;
  const shouldRenderErrorLoader = isError && errorPlaceholder;

  useEffect(() => {
    if (!shallowEqual(previousUrls, urls)) {
      setImageState(defaultImgState);
    }
  }, [previousUrls, urls]);

  useEffect(() => {
    imgLib?.current?.mount();

    return () => {
      imgLib?.current?.unmount();
    };
  }, []);

  if (shouldRenderLoader) {
    return (
      <Loader className={className} customLoader={customLoader} forwardedRef={ref} type={loader} />
    );
  }

  if (shouldRenderErrorLoader) {
    return <ErrorPlaceholder className={className} forwardedRef={ref} type={errorPlaceholder} />;
  }
  return node;
};

const mapDispatchToProps = {
  trackEvent: _trackEvent,
};

const ConnectedSmartImage = connect(
  null,
  mapDispatchToProps
)(
  memo(SmartImage, (prevProps, nextProps) =>
    shallowEqualNested(prevProps, nextProps, ['urls', 'style'])
  )
);

export default ConnectedSmartImage;
export const ViewportSmartImage = (props) => <ConnectedSmartImage {...props} loading="lazy" />;
// Exporting unconnected version for static site
export const UnconnectedViewportSmartImage = (props) => <SmartImage {...props} loading="lazy" />;
